import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import type { PageResponse } from '@/lib/types';
import type { ModerationCandidate } from '@/lib/queries/moderation';
import { EmptyState } from '@/components/molecules/empty-state';

export type ModerationQueuePaneProps = {
  page: PageResponse<ModerationCandidate> | undefined;
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
};

const COMPOSER_FALLBACK = 'Traditional';
const SUBMITTER_FALLBACK = 'Unknown';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_WEEK = MS_PER_DAY * 7;

function formatAge(submittedAt: string): string {
  const elapsed = Date.now() - new Date(submittedAt).getTime();
  if (elapsed < MS_PER_HOUR) return `${Math.max(1, Math.floor(elapsed / MS_PER_MINUTE))}m`;
  if (elapsed < MS_PER_DAY) return `${Math.floor(elapsed / MS_PER_HOUR)}h`;
  if (elapsed < MS_PER_WEEK) return `${Math.floor(elapsed / MS_PER_DAY)}d`;
  return `${Math.floor(elapsed / MS_PER_WEEK)}w`;
}

export function ModerationQueuePane({
  page, isLoading, currentPage, onPageChange,
}: ModerationQueuePaneProps): ReactElement {
  if (isLoading || page === undefined) {
    return (
      <div className="p-6 text-[12px] text-[var(--ink-3)]">Loading…</div>
    );
  }

  if (page.totalElements === 0) {
    return (
      <EmptyState
        title="Queue is clear"
        description="No submissions to review."
      />
    );
  }

  const hasPagination = page.totalPages > 1;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage + 1 >= page.totalPages;

  return (
    <div className="flex flex-col h-full border-r border-[var(--rule)]">
      <ul className="flex-1 overflow-auto divide-y divide-[var(--rule)]">
        {page.content.map((candidate) => (
          <li key={candidate.pieceId}>
            <Link
              to="/admin/queue/$pieceId"
              params={{ pieceId: candidate.pieceId }}
              className="block px-4 py-3 hover:bg-[var(--bg-soft)] focus:bg-[var(--bg-soft)] focus:outline-none"
            >
              <div className="text-[13px] font-semibold text-[var(--ink)] truncate">
                {candidate.prettyName}
              </div>
              <div className="text-[12px] text-[var(--ink-3)] truncate">
                {candidate.composerName ?? COMPOSER_FALLBACK}
                {candidate.formsLabel ? ` · ${candidate.formsLabel}` : ''}
              </div>
              <div className="text-[11px] text-[var(--ink-4)] mt-0.5">
                {candidate.submitterHandle ?? SUBMITTER_FALLBACK}
                {' · '}
                {formatAge(candidate.submittedAt)}
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {hasPagination && (
        <nav
          aria-label="Moderation queue pages"
          className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[var(--rule)] text-[12px] text-[var(--ink-3)]"
        >
          <button
            type="button"
            disabled={isFirstPage}
            onClick={() => onPageChange(currentPage - 1)}
            className="px-2 py-1 rounded-[var(--r-sm)] hover:bg-[var(--bg-soft)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹ Previous
          </button>
          <span>
            Page {currentPage + 1} of {page.totalPages}
          </span>
          <button
            type="button"
            disabled={isLastPage}
            onClick={() => onPageChange(currentPage + 1)}
            className="px-2 py-1 rounded-[var(--r-sm)] hover:bg-[var(--bg-soft)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
        </nav>
      )}
    </div>
  );
}
